import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_RESOURCE_CATEGORY_TREE } from '../../../gql/queries/resourceCategoryQuery'
import { IResourceCategory, IResourceCategoryTreeResponse } from '../../../types/resourceCategory'
import { useDisclourse } from '../../../hooks/useDisclourse'
import { buildCategoryChildrenMap } from '../categoryTreeHelpers'
import { CategoryTreeNode } from './CategoryTreeNode'
import { ModalAddCategory } from './ModalAddCategory'
import { ModalEditCategory } from './ModalEditCategory'
import { ModalDeleteCategory } from './ModalDeleteCategory'

interface ICategoryTreeProps {
  selectedCategoryId: string | null
  onSelectCategory: (categoryId: string | null) => void
  // Notifica al padre la lista plana de categorías (para breadcrumbs y pickers)
  onCategoriesLoaded?: (categories: IResourceCategory[]) => void
  // Mueve recursos a una carpeta (null = sin categoría) al soltarlos sobre un nodo
  onDropResources?: (categoryId: string | null, resourceIds: string[]) => Promise<void> | void
}

export const CategoryTree: React.FC<ICategoryTreeProps> = ({
  selectedCategoryId,
  onSelectCategory,
  onCategoriesLoaded,
  onDropResources,
}) => {
  const { onOpen: onOpenAdd, isOpen: isOpenAdd, onClose: onCloseAdd } = useDisclourse()
  const { onOpen: onOpenEdit, isOpen: isOpenEdit, onClose: onCloseEdit } = useDisclourse()
  const { onOpen: onOpenDelete, isOpen: isOpenDelete, onClose: onCloseDelete } = useDisclourse()

  const [addParentId, setAddParentId] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<IResourceCategory | null>(null)

  const { data, loading, refetch } = useQuery<IResourceCategoryTreeResponse>(
    GET_RESOURCE_CATEGORY_TREE,
    {
      fetchPolicy: 'network-only',
    }
  )

  const categories = useMemo(() => data?.resourceCategoryTree || [], [data])

  // useEffect (no onCompleted): así el padre también se entera tras cada refetch
  useEffect(() => {
    if (data) onCategoriesLoaded?.(data.resourceCategoryTree || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // Tras mover recursos, refrescar contadores del árbol
  const handleDropResources = async (categoryId: string | null, resourceIds: string[]) => {
    await onDropResources?.(categoryId, resourceIds)
    refetch()
  }

  const [isDragOverAll, setIsDragOverAll] = useState(false)
  const childrenMap = useMemo(() => buildCategoryChildrenMap(categories), [categories])
  const rootCategories = childrenMap.get(null) || []

  const handleAddRoot = () => {
    setAddParentId(null)
    onOpenAdd()
  }

  const handleAddSubfolder = (parentId: string) => {
    setAddParentId(parentId)
    onOpenAdd()
  }

  const handleEditCategory = (category: IResourceCategory) => {
    setSelectedCategory(category)
    onOpenEdit()
  }

  const handleDeleteCategory = (category: IResourceCategory) => {
    setSelectedCategory(category)
    onOpenDelete()
  }

  const handleRefetch = () => {
    refetch()
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center py-3">
        <h6 className="mb-0">
          <i className="bi bi-folder me-2 text-warning" /> Folders
        </h6>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={handleAddRoot}
          title="New folder"
        >
          <i className="bi bi-folder-plus" />
        </button>
      </div>

      <div
        className="card-body p-2"
        style={{
          overflowY: 'auto',
          // Con el card sticky, el árbol scrollea internamente si es muy largo
          maxHeight: 'calc(100vh - var(--bs-app-header-height, 70px) - 140px)',
        }}
      >
        {/* Nodo "todos" — soltar aquí quita la carpeta (sin categoría) */}
        <div
          className={`d-flex align-items-center gap-2 rounded px-2 py-1 mb-1 ${
            selectedCategoryId === null ? 'bg-primary bg-opacity-10' : ''
          } ${isDragOverAll ? 'bg-warning bg-opacity-25 border border-warning' : ''}`}
          style={{ cursor: 'pointer' }}
          onClick={() => onSelectCategory(null)}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes('application/x-resource-ids')) {
              e.preventDefault()
              setIsDragOverAll(true)
            }
          }}
          onDragLeave={() => setIsDragOverAll(false)}
          onDrop={(e) => {
            e.preventDefault()
            setIsDragOverAll(false)
            const raw = e.dataTransfer.getData('application/x-resource-ids')
            if (raw) handleDropResources(null, JSON.parse(raw))
          }}
        >
          <span style={{ width: 16 }} />
          <i className={`bi bi-collection ${selectedCategoryId === null ? 'text-primary' : 'text-muted'}`} />
          <span className={selectedCategoryId === null ? 'fw-bold text-primary' : ''}>
            All resources
          </span>
        </div>

        {loading ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : rootCategories.length === 0 ? (
          <div className="text-center text-muted small py-3">
            <i className="bi bi-folder-x d-block mb-1" style={{ fontSize: 24 }} />
            No folders yet.
            <br />
            Create one to organize your resources.
          </div>
        ) : (
          rootCategories.map((category) => (
            <CategoryTreeNode
              key={category.id}
              category={category}
              childrenMap={childrenMap}
              selectedCategoryId={selectedCategoryId}
              onSelectCategory={onSelectCategory}
              onAddSubfolder={handleAddSubfolder}
              onEditCategory={handleEditCategory}
              onDeleteCategory={handleDeleteCategory}
              onDropResources={handleDropResources}
            />
          ))
        )}
      </div>

      {isOpenAdd && (
        <ModalAddCategory
          isOpen={isOpenAdd}
          onClose={onCloseAdd}
          refetch={handleRefetch}
          categories={categories}
          parentId={addParentId}
        />
      )}

      {isOpenEdit && selectedCategory && (
        <ModalEditCategory
          isOpen={isOpenEdit}
          onClose={onCloseEdit}
          refetch={handleRefetch}
          categories={categories}
          category={selectedCategory}
        />
      )}

      {isOpenDelete && selectedCategory && (
        <ModalDeleteCategory
          isOpen={isOpenDelete}
          onClose={onCloseDelete}
          refetch={() => {
            handleRefetch()
            // Si la carpeta eliminada estaba seleccionada, volver a "todos"
            if (selectedCategoryId === selectedCategory.id) {
              onSelectCategory(null)
            }
          }}
          category={selectedCategory}
        />
      )}
    </div>
  )
}
